import { useEffect, useState, useCallback } from "react";
import Layout from "../../layout/Layout";
import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  getStatisticsForAllSubjects,
  getStatisticsFilterOptions,
} from "../../services/apiCalls.js";
import DistributionPieChartComponent from "../../components/Statistics/HomePageStatistics/DistributionPieChartComponent.jsx";
import DistributionBarChartComponent from "../../components/Statistics/HomePageStatistics/DistributionBarChartComponent.jsx";
import StatisticsFilterPanel from "../../components/Statistics/HomePageStatistics/StatisticsFilterPanel.jsx";
import { getChartColor } from "../../components/Statistics/HomePageStatistics/chartUtilies.js";

const Home = () => {
  const [generalStatistics, setGeneralStatistics] = useState([]);
  const [totalCitizens, setTotalCitizens] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState({
    sex: "",
    age_min: null,
    age_max: null,
    country: "",
    education: "",
    region: "",
    risk_level: "",
  });

  const { t } = useTranslation("homepage");

  const clinicianId = localStorage.getItem("clinician_id");

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const optionsData = await getStatisticsFilterOptions(clinicianId);
        if (optionsData?.filter_options) {
          setFilterOptions(optionsData.filter_options);
          // Initialize age range from options
          if (optionsData.filter_options.age_range) {
            setFilters((prev) => ({
              ...prev,
              age_min: optionsData.filter_options.age_range.min,
              age_max: optionsData.filter_options.age_range.max,
            }));
          }
        }
      } catch (error) {
        console.error("Error when fetching filter options", error);
      }
    };
    fetchFilterOptions();
  }, [clinicianId]);

  // Fetch statistics data
  const fetchGeneralStatisticsData = useCallback(async () => {
    try {
      // Build filters object, removing empty values
      const activeFilters = {};
      if (filters.sex) activeFilters.sex = filters.sex;
      if (filters.age_min !== null && filters.age_min !== filterOptions.age_range?.min) {
        activeFilters.age_min = filters.age_min;
      }
      if (filters.age_max !== null && filters.age_max !== filterOptions.age_range?.max) {
        activeFilters.age_max = filters.age_max;
      }
      if (filters.country) activeFilters.country = filters.country;
      if (filters.education) activeFilters.education = filters.education;
      if (filters.region) activeFilters.region = filters.region;
      if (filters.risk_level !== "" && filters.risk_level !== null) {
        activeFilters.risk_level = filters.risk_level;
      }

      const statisticsData = await getStatisticsForAllSubjects(clinicianId, activeFilters);
      setGeneralStatistics(statisticsData?.statistics || {});
      setTotalCitizens(statisticsData?.total_citizens ?? 0);
    } catch (error) {
      console.error("Error when fetching general statistics in Home component", error);
    }
  }, [clinicianId, filters, filterOptions.age_range]);

  // Initial fetch and refetch when filters change
  useEffect(() => {
    fetchGeneralStatisticsData();
  }, [fetchGeneralStatisticsData]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <Layout>
      <Typography
        sx={{
          marginTop: "50px",
          marginLeft: "15px",
          marginBottom: "25px",
          fontSize: {
            xs: "1.3rem",
            sm: "2rem",
          },
          textAlign: "center",
        }}
        variant="h4"
      >
        {t("heading.welcome")}
      </Typography>

      {/* Filter Panel */}
      <StatisticsFilterPanel
        filterOptions={filterOptions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <Typography
        sx={{
          textAlign: "center",
          marginTop: "30px",
          fontSize: "1.1rem",
          color: "text.secondary",
          fontWeight: 500,
        }}
      >
        {t("heading.totalCitizens", { count: totalCitizens })}
      </Typography>

      {/* <Grid
        container
        spacing={2}
        justifyContent="center"
        sx={{
          padding: {
            sm: "0 20px",
            xl: "unset",
          },
        }}
      >
        <Grid item xs={6} xl={2}>
          <HomeCard
            cardTitle="Total assigned citizens"
            number="250"
            bgLinear="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
          />
        </Grid>
        <Grid item xs={6} xl={2}>
          <HomeCard
            cardTitle="New recommendations"
            number="6"
            bgLinear="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
          />
        </Grid>
        <Grid item xs={6} xl={2}>
          <HomeCard
            cardTitle="New Notifications"
            number="7"
            bgLinear="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
          />
        </Grid>
        <Grid item xs={6} xl={2}>
          <HomeCard
            cardTitle="New Notifications"
            number="7"
            bgLinear="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
          />
        </Grid>
      </Grid> */}

      <Grid
        container
        sx={{
          marginTop: "10vh",
          flexDirection: { xs: "column", lg: "row", xl: "row" },
          alignItems: { xs: "center", lg: "center" },
          justifyContent: { lg: "center" },
          overflow: "hidden",
        }}
        spacing={0}
      >
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionPieChartComponent
            data={generalStatistics?.activity_distribution}
            title={t("charts.activityDistribution")}
            tKeyPrefix="activity"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionBarChartComponent
            data={generalStatistics?.bmi_distribution}
            color={getChartColor()[0]}
            title={t("charts.bmiDistribution")}
            tKeyPrefix="bmi"
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionPieChartComponent
            data={generalStatistics?.country_distribution}
            title={t("charts.countryDistribution")}
            tKeyPrefix="countries"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionPieChartComponent
            data={generalStatistics?.education_distribution}
            color={getChartColor()[1]}
            title={t("charts.educationDistribution")}
            tKeyPrefix="education"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionPieChartComponent
            data={generalStatistics?.environment_distribution}
            title={t("charts.environmentDistribution")}
            tKeyPrefix="environment"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionPieChartComponent
            data={generalStatistics?.ethnicity_distribution}
            color={getChartColor()[2]}
            title={t("charts.ethnicityDistribution")}
            tKeyPrefix="ethnicity"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionPieChartComponent
            data={generalStatistics?.gender_distribution}
            title={t("charts.genderDistribution")}
            tKeyPrefix="gender"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionBarChartComponent
            data={generalStatistics?.risk_level_distribution}
            color={getChartColor()[3]}
            title={t("charts.riskLevelDistribution")}
            tKeyPrefix="risk"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          sx={{
            marginBottom: {
              xs: "30px",
              lg: "50px",
            },
          }}
        >
          <DistributionPieChartComponent
            data={generalStatistics?.smoking_distribution}
            title={t("charts.smokingDistribution")}
            tKeyPrefix="smoking"
          />
        </Grid>
      </Grid>
    </Layout>
  );
};

export default Home;
